"use client";

import { useLang } from "@/contexts/language-context";
import { motion } from "framer-motion";
import { Bell } from "lucide-react";
import Link from "next/link";

export default function FoodTruckQsrSmsPullback() {
  const { t } = useLang();

  return (
    <section className="py-24 px-6 bg-redy-charcoal text-white relative overflow-hidden">
      <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-redy-orange/15 rounded-full blur-[140px] pointer-events-none" />
      <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-redy-red/10 rounded-full blur-[120px] pointer-events-none" />

      <div className="relative max-w-[1100px] mx-auto grid grid-cols-1 md:grid-cols-[1.2fr_1fr] gap-12 items-center">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.5 }}
        >
          <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-redy-orange mb-4">
            {t("They walk away", "Se van caminando")}
          </div>
          <h2 className="text-[40px] md:text-[60px] font-black leading-[1.02] tracking-tight mb-5">
            {t("They leave the window.", "Salen de la ventana.")}
            <br />
            <span className="redy-text-gradient">
              {t("We bring them back.", "Los regresamos.")}
            </span>
          </h2>
          <p className="text-lg text-white/70 leading-relaxed mb-8 max-w-[480px]">
            {t(
              "Food truck rules: nobody waits at the window. They order, pay, and walk to their car or a picnic table. The second the kitchen marks it ready, the customer's phone buzzes. They walk back. No screen-watching, no name-shouting.",
              "Regla de food truck: nadie espera en la ventana. Piden, pagan, y caminan al carro o a una mesa. En el momento que la cocina marca listo, el teléfono del cliente vibra. Regresa. Sin mirar pantallas, sin gritar nombres."
            )}
          </p>

          <div className="flex flex-wrap gap-3 mb-8">
            {[
              { en: "Order ready", es: "Pedido listo" },
              { en: "On the line", es: "En la línea" },
              { en: "Out for delivery", es: "Salió" },
              { en: "Pickup ready", es: "Listo para recoger" },
            ].map((tag, i) => (
              <span
                key={i}
                className="text-xs font-bold bg-white/10 border border-white/10 px-3 py-1.5 rounded-full"
              >
                {t(tag.en, tag.es)}
              </span>
            ))}
          </div>

          <Link
            href="/features/sms-customers-staff"
            className="inline-flex items-center gap-2 text-sm font-bold text-redy-orange hover:text-white transition-colors"
          >
            {t("How SMS notifications work →", "Cómo funcionan los SMS →")}
          </Link>
        </motion.div>

        {/* iPhone mockup */}
        <motion.div
          initial={{ opacity: 0, scale: 0.94 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.6, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
          className="relative mx-auto w-full max-w-[300px]"
        >
          <div className="relative bg-black rounded-[44px] p-3 shadow-[0_30px_80px_-20px_rgba(244,114,43,0.4)]">
            <div className="bg-[#1A1A1F] rounded-[36px] overflow-hidden">
              {/* iPhone status bar */}
              <div className="flex items-center justify-between px-7 pt-3 pb-1 text-white text-xs font-semibold">
                <span>11:47</span>
                <div className="w-24 h-5 bg-black rounded-full" />
                <span>●●●</span>
              </div>

              <div className="px-3 py-6 min-h-[460px] flex flex-col gap-3 items-center justify-center">
                {/* Lock-screen style notifications */}
                <motion.div
                  initial={{ opacity: 0, y: -20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: 0.5 }}
                  className="w-full bg-white/10 backdrop-blur-md border border-white/10 rounded-2xl px-4 py-3 text-white"
                >
                  <div className="flex items-center gap-2 mb-1">
                    <div className="w-7 h-7 bg-redy-orange rounded-md flex items-center justify-center text-xs font-black">
                      R
                    </div>
                    <div className="flex-1 flex items-center justify-between">
                      <span className="text-xs font-bold">REDY</span>
                      <span className="text-[10px] text-white/50">now</span>
                    </div>
                  </div>
                  <p className="text-sm leading-snug">
                    {t(
                      "Your birria is ready #042 🌮 Come pick it up.",
                      "Tu birria está lista #042 🌮 Pásala a recoger."
                    )}
                  </p>
                </motion.div>

                <motion.div
                  initial={{ opacity: 0, y: -20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: 0.85 }}
                  className="w-full bg-white/[0.06] backdrop-blur-md border border-white/[0.06] rounded-2xl px-4 py-3 text-white/80"
                >
                  <div className="flex items-center gap-2 mb-1">
                    <div className="w-6 h-6 bg-redy-orange/80 rounded-md flex items-center justify-center text-[10px] font-black">
                      R
                    </div>
                    <div className="flex-1 flex items-center justify-between">
                      <span className="text-[11px] font-bold">REDY</span>
                      <span className="text-[10px] text-white/40">3m ago</span>
                    </div>
                  </div>
                  <p className="text-xs leading-snug">
                    {t(
                      "On the line. ETA 2 min ⏱",
                      "En la línea. ETA 2 min ⏱"
                    )}
                  </p>
                </motion.div>

                <motion.div
                  initial={{ opacity: 0 }}
                  whileInView={{ opacity: 1 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: 1.2 }}
                  className="mt-auto self-center inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider text-white/50"
                >
                  <Bell className="w-3 h-3" />
                  {t("3 alerts · 1 customer", "3 alertas · 1 cliente")}
                </motion.div>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
